import React from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { color } from '../../styles';
import { CareRecordFormHook } from '@/hooks/useCareRecordForm';
import { DateSelector } from '../mui/dateSelector';
import { TimeSelector } from '../mui/timeSelector';
import { OutlineButton } from '../outlineButton';

interface Props {
  form: CareRecordFormHook;
  onClose: () => void;
  onSubmit: () => void;
  onOpenResetModal: () => void;
  showModeToggle?: boolean;
  children: React.ReactNode;
}

const AlignCenter = css`
  display: flex;
  align-items: center;
`;

const ContentGap = css`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const DateContainer = css`
  display: flex;
  align-items: center;
  width: 208px;
`;

const TimeContainer = css`
  display: flex;
  align-items: center;
  width: 192px;
`;

const MarginBottom = css`
  margin-bottom: 24px;
`;

const Container = css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const UpperContainer = css`
  display: flex;
  justify-content: space-evenly;
  min-width: 580px;
  width: 100%;
  gap: 8px;
`;

const HeaderLabel = css`
  margin-right: 8px;
  font-size: 16px;
  color: ${color.dark_gray};
  line-height: 1em;
  flex-shrink: 0;
`;

const FlexColumn = css`
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  &.MuiButton-outlined {
    background-color: ${color.white};
    color: ${color.dark_gray};
    border: 1px solid ${color.dark_gray};
  }
`;

export const CareRecordFormFrame = ({
  form,
  onClose,
  onSubmit,
  onOpenResetModal,
  showModeToggle = false,
  children,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div css={Container}>
      <div css={[UpperContainer, AlignCenter, MarginBottom]}>
        <div css={DateContainer}>
          <div css={HeaderLabel}>{t('date', '日付 : ')}</div>
          <DateSelector
            newValue={form.date}
            label={''}
            minDate={form.now.subtract(1, 'years')}
            maxDate={form.now}
            variant={'outlined'}
            onChange={form.handleChangeDate}
          />
        </div>
        <div css={TimeContainer}>
          <div css={HeaderLabel}>{t('excretionTimeLabel', '排泄時刻 : ')}</div>
          <TimeSelector newValue={form.date} label={''} variant={'outlined'} onChange={form.handleChangeTime} />
        </div>
        {showModeToggle && (
          <OutlineButton
            onClick={form.handleChangeRecordButton}
            icon={<img src='/images/residents/recordModal/change.svg' alt='' />}
          >
            {form.isDisplayAdvancedForm ? t('basicRecord', '簡単記録') : t('detailedRecord', '詳細記録')}
          </OutlineButton>
        )}
      </div>

      {children}

      <div css={[FlexColumn, ContentGap]}>
        <div css={ContentGap}>
          <Button variant='contained' disableElevation color='secondary' onClick={onClose}>
            {t('cancel', 'キャンセル')}
          </Button>
          <Button variant='contained' disabled={form.disabledSubmit} disableElevation onClick={onSubmit}>
            {t('record', '記録する')}
          </Button>
        </div>
        <div>
          <StyledButton variant='outlined' disableElevation onClick={onOpenResetModal}>
            {t('setTo0WithoutRecordingUrination', '排尿記録せずに0にする')}
          </StyledButton>
        </div>
      </div>
    </div>
  );
};
